/**
 * =============================================================================
 * useMedia — media moderation review-queue data hook (ADR-022 §10 / PR4)
 * =============================================================================
 *
 * Hook over `adminApi.media.*` (BL-IMG-ADMIN-MODERATION-001). Owns the loaded
 * queue rows + offset pagination + per-status counts and exposes:
 *
 *   - `applyFilters(filters)` — (re)load page 1 with the given filters,
 *     replacing loaded rows. Lazy: no fetch on mount; the panel drives the
 *     first load.
 *   - `goToPage(page)`        — load another page of the ACTIVE filters.
 *   - `moderate(...)`         — apply one transition, then reload the current
 *     page (the row's status — and possibly its filter membership — changed).
 *   - `moderateBulk(...)`     — one action over selected ids with per-item
 *     results; reloads and returns the summary for toast reporting.
 *   - `remove(...)`           — §10(B) admin soft-delete (PR4b), then reload;
 *     a D-LEGALHOLD 409 resolves to the explicit hold message.
 *
 * List requests run under an AbortController (a newer request supersedes an
 * in-flight one — the useAudit pattern); mutations are awaited singly and
 * serialized by the panel's pending-state guard.
 * =============================================================================
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  AdminDeleteMediaResponse,
  AdminMediaFilters,
  AdminMediaListItem,
  BulkModerateMediaResponse,
  MediaModerationAction,
  MediaModerationStatus,
  MediaRejectionReason,
  ModerateMediaResponse,
  PaginationMeta,
} from '@find-my-part/types';
import { adminApi } from '../api/client';
import { resolveMediaErrorMessage } from './useMediaAuthoring';

/** Page size for the media review queue (backend clamps to [1, 100]). */
export const MEDIA_PAGE_LIMIT = 20;

type MediaQueueFilters = Pick<AdminMediaFilters, 'status' | 'questionId'>;

export interface UseMediaResult {
  media: AdminMediaListItem[];
  pagination: PaginationMeta | null;
  /** Store-wide per-status counts (filter chips + pending badge). */
  counts: Record<MediaModerationStatus, number> | null;
  isLoading: boolean;
  /** A moderate/moderateBulk call in flight. */
  isMutating: boolean;
  /** User-facing error message, or null. Never set for a superseded request. */
  error: string | null;
  /** True once the first successful load has completed (drives lazy UI). */
  hasLoaded: boolean;
  /** (Re)load page 1 with `filters`, replacing loaded rows. */
  applyFilters: (filters: MediaQueueFilters) => Promise<void>;
  /** Load `page` of the active filters. */
  goToPage: (page: number) => Promise<void>;
  /** Reload the current page + counts (post-mutation sync). */
  refresh: () => Promise<void>;
  /** Apply one moderation transition, then refresh. */
  moderate: (
    attachmentId: string,
    action: MediaModerationAction,
    reason?: MediaRejectionReason
  ) => Promise<{ success: boolean; message: string; data?: ModerateMediaResponse }>;
  /** Apply one action to many attachments (per-item results), then refresh. */
  moderateBulk: (
    attachmentIds: string[],
    action: MediaModerationAction,
    reason?: MediaRejectionReason
  ) => Promise<{ success: boolean; message: string; data?: BulkModerateMediaResponse }>;
  /**
   * Soft-delete one attachment (ADR-022 §10(B) admin remove — PR4b), then
   * refresh. A D-LEGALHOLD 409 resolves to the explicit hold message.
   */
  remove: (
    attachmentId: string
  ) => Promise<{ success: boolean; message: string; data?: AdminDeleteMediaResponse }>;
}

function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

export function useMedia(token: string): UseMediaResult {
  const [media, setMedia] = useState<AdminMediaListItem[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [counts, setCounts] = useState<Record<MediaModerationStatus, number> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Filters + page the loaded rows were fetched with; mutations/refresh reuse
  // them so a reload always matches what is on screen.
  const activeFiltersRef = useRef<MediaQueueFilters>({});
  const activePageRef = useRef(1);
  const abortRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const load = useCallback(
    async (filters: MediaQueueFilters, page: number): Promise<void> => {
      if (!token) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsLoading(true);
      setError(null);

      try {
        const res = await adminApi.media.list(
          token,
          { ...filters, page, limit: MEDIA_PAGE_LIMIT },
          controller.signal
        );
        if (!isMountedRef.current || abortRef.current !== controller) return;
        if (res.success) {
          activeFiltersRef.current = filters;
          activePageRef.current = page;
          setMedia(res.data.media);
          setPagination(res.data.pagination);
          setCounts(res.data.counts);
          setHasLoaded(true);
        } else {
          setError(res.error?.message ?? 'Failed to load the media review queue.');
        }
      } catch (err) {
        if (isAbortError(err)) return; // superseded — silently ignore
        if (!isMountedRef.current || abortRef.current !== controller) return;
        setError('Failed to load the media review queue. Please try again.');
      } finally {
        if (isMountedRef.current && abortRef.current === controller) {
          setIsLoading(false);
        }
      }
    },
    [token]
  );

  const applyFilters = useCallback(
    (filters: MediaQueueFilters): Promise<void> => load(filters, 1),
    [load]
  );

  const goToPage = useCallback(
    (page: number): Promise<void> => load(activeFiltersRef.current, page),
    [load]
  );

  const refresh = useCallback(
    (): Promise<void> => load(activeFiltersRef.current, activePageRef.current),
    [load]
  );

  const moderate = useCallback(
    async (
      attachmentId: string,
      action: MediaModerationAction,
      reason?: MediaRejectionReason
    ): Promise<{ success: boolean; message: string; data?: ModerateMediaResponse }> => {
      if (!token) return { success: false, message: 'Not authenticated.' };
      setIsMutating(true);
      try {
        const res = await adminApi.media.moderate(token, attachmentId, action, reason);
        if (res.success) {
          await refresh();
          return { success: true, message: res.data.message, data: res.data };
        }
        // A SHOW on a held row 409s with details.legalHold (D-LEGALHOLD veto —
        // re-publishing held evidence is never allowed); surface the explicit
        // hold explanation instead of a generic transition conflict.
        return {
          success: false,
          message: resolveMediaErrorMessage(res.error, {
            fallback: 'Failed to apply the moderation action.',
          }),
        };
      } catch {
        return { success: false, message: 'Failed to apply the moderation action.' };
      } finally {
        if (isMountedRef.current) {
          setIsMutating(false);
        }
      }
    },
    [token, refresh]
  );

  const moderateBulk = useCallback(
    async (
      attachmentIds: string[],
      action: MediaModerationAction,
      reason?: MediaRejectionReason
    ): Promise<{ success: boolean; message: string; data?: BulkModerateMediaResponse }> => {
      if (!token) return { success: false, message: 'Not authenticated.' };
      setIsMutating(true);
      try {
        const res = await adminApi.media.moderateBulk(token, {
          attachmentIds,
          action,
          reason,
        });
        if (res.success) {
          await refresh();
          const { summary } = res.data;
          const message =
            summary.failed === 0
              ? `${summary.succeeded} of ${summary.total} item(s) processed.`
              : `${summary.succeeded} of ${summary.total} item(s) processed; ${summary.failed} failed.`;
          return { success: summary.failed === 0, message, data: res.data };
        }
        return {
          success: false,
          message: res.error?.message ?? 'Failed to apply the bulk moderation action.',
        };
      } catch {
        return { success: false, message: 'Failed to apply the bulk moderation action.' };
      } finally {
        if (isMountedRef.current) {
          setIsMutating(false);
        }
      }
    },
    [token, refresh]
  );

  const remove = useCallback(
    async (
      attachmentId: string
    ): Promise<{ success: boolean; message: string; data?: AdminDeleteMediaResponse }> => {
      if (!token) return { success: false, message: 'Not authenticated.' };
      setIsMutating(true);
      try {
        const res = await adminApi.media.remove(token, attachmentId);
        if (res.success) {
          await refresh();
          return { success: true, message: res.data.message, data: res.data };
        }
        // D-LEGALHOLD 409 maps to the explicit hold copy; other errors keep
        // the server message (admin surface — no anti-tip-off constraint).
        return {
          success: false,
          message: resolveMediaErrorMessage(res.error, {
            fallback: 'Failed to remove the image.',
          }),
        };
      } catch {
        return { success: false, message: 'Failed to remove the image.' };
      } finally {
        if (isMountedRef.current) {
          setIsMutating(false);
        }
      }
    },
    [token, refresh]
  );

  return {
    media,
    pagination,
    counts,
    isLoading,
    isMutating,
    error,
    hasLoaded,
    applyFilters,
    goToPage,
    refresh,
    moderate,
    moderateBulk,
    remove,
  };
}
